import { DatePicker, Input, Modal, Popover, Select, Spin, Table, Tabs, message } from "antd";
import SuccessDeclinePopup from "reusableComponent/Popup/SuccessDeclinePopup";
import AdjustDatesModal from "./AdjustDatesModal";
import GenerateListModal from "./GenerateListModal";
import { SearchOutlined } from "@mui/icons-material";
import {
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  CalendarOutlined,
  InfoCircleFilled,
} from "@ant-design/icons";
import COLORS from "common/Colors";
import { ButtonFilled } from "reusableComponent/UIButtons";
import TextLink from "./TextLink";
import { CALL_API, CALL_API_WITH_HEADER } from "common/API";
import { STATUS_CODE } from "common/Constants";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { useHistory, useLocation } from "react-router-dom/cjs/react-router-dom.min";
import { getUserInfo, getClientid, getMenuList, getUserRoleId } from "redux/selectors";
import { isHrKpiMenu } from "./hrKpiMenu";
import {
  KPI_PLANNING_MINE_STATUS,
  KPI_PLANNING_MINE_ACTIONS,
  KPI_PLANNING_MYTEAM_STATUS,
  KPI_PLANNING_MYTEAM_ACTIONS,
  statusMeta,
} from "./statusTag";

// A contract sitting in "accepted" (non-self-KPI: the manager already
// defined the plan, so there's no separate KPI-review step) or
// "inprogressreview" (between checkpoints) is waiting on the next
// not-yet-completed progress-review checkpoint (Q1 / Mid-Year / Q3). Once
// that date arrives, it should read as "Awaiting Progress Review" -
// computed on read here rather than via a scheduled backend job.
const CHECKPOINT_WAITING_STATUSES = ["accepted", "inprogressreview"];

// The Mine tab only ever shows the 6 appraisee-facing statuses from design -
// these three progress-review-cycle statuses are My Team-only and collapse
// to "Accepted" on Mine.
const MINE_HIDDEN_STATUSES = ["awaitingprogressreview", "inprogressreview", "reviewsclosed"];
const isCheckpointDue = (record) => {
  if (!CHECKPOINT_WAITING_STATUSES.includes(record.planningstatus)) return false;
  const next = (record.progressreviewdates || []).find((item) => !item.completed);
  return Boolean(next?.date) && !dayjs(next.date).startOf("day").isAfter(dayjs().startOf("day"));
};

const deriveStatus = (record) =>
  isCheckpointDue(record) ? "awaitingprogressreview" : record.planningstatus;

// Employees with self-KPI enabled skip the "Not Yet Created" / "Create KPI" step
// entirely - they submit their own plan, so the row should read as already
// awaiting their submission.
const deriveMyTeamStatus = (record) => {
  if (record.planningstatus === "notyetcreated" && record.isemployeeselfkpi) {
    return "awaitingkpisubmission";
  }
  return deriveStatus(record);
};

const applyDerivedStatus = (list) =>
  list.map((record) => ({ ...record, planningstatus: deriveMyTeamStatus(record) }));

// Statuses that mean the employee's current cycle is still open - a new
// contract can't be generated for them until it closes.
const OPEN_PLANNING_STATUSES = [
  "yettoaccept",
  "accepted",
  "awaitingkpisubmission",
  "awaitingkpireview",
  "revisionrequested",
  "awaitingprogressreview",
  "inprogressreview",
];

const blankTeamRow = (report) => ({
  id: `notyetcreated-${report.userid}`,
  appraiseeuserid: report.userid,
  appraiseruserid: report.reportingmanager,
  fullname: report.fullname,
  employeeid: report.employeeid,
  period: null,
  createdon: null,
  progressreviewdates: [],
  startdate: null,
  planningstatus: "notyetcreated",
  isemployeeselfkpi: report.isemployeeselfkpi,
});

// Redux's userList is only populated once at login (App.js's fetchMasterData)
// and never refreshed, so an isemployeeselfkpi toggle made afterwards in
// People never reaches it. Fetch the roster fresh here instead so this
// screen always reflects the current People data.
// add-users is gated on the People "employee" view permission, which roles
// like Reporting Manager don't have (it returns 401 and the team list came
// up empty) - fall back to master-data, which returns the same fresh roster
// (including isemployeeselfkpi) for any logged-in role.
const fetchEmployeeList = async (clientid) => {
  const { response } = await CALL_API_WITH_HEADER(
    `add-users/${clientid}`,
    "get",
    {},
    { menupath: "employee" }
  );
  if (Array.isArray(response)) return response;
  const { userList } = await CALL_API(`master-data/${clientid}`, "post", {
    fetch_master_data: true,
  });
  return Array.isArray(userList) ? userList : [];
};

// The deployed backend has no organisation-wide list with the fields the HR
// tab needs (hrcontracts/all only returns a few columns), so the HR tab is
// assembled from each reporting manager's own My Team list.
const managersOf = (employees) => [
  ...new Set(
    (employees || [])
      .filter((user) => user?.statusid === "1")
      .map((user) => user?.reportingmanager)
      .filter(Boolean)
  ),
];

const fetchAllTeamPlanning = async (employees) => {
  const managers = managersOf(employees);
  const [teamLists, all] = await Promise.all([
    Promise.all(
      managers.map((manager) =>
        CALL_API(`performance-kpi-management/myteamplanning/${manager}`, "get")
          .then(({ data, code }) => (code === STATUS_CODE.SUCCESS && Array.isArray(data) ? data : []))
          .catch(() => [])
      )
    ),
    CALL_API("performance-kpi-management/hrcontracts/all", "get")
      .then(({ data, code }) => (code === STATUS_CODE.SUCCESS && Array.isArray(data) ? data : []))
      .catch(() => []),
  ]);
  const byId = new Map();
  teamLists.flat().forEach((contract) => byId.set(contract.id, contract));
  // A contract whose appraiser is no longer anyone's reporting manager isn't
  // in any team list - keep it from the short hrcontracts/all row, matched to
  // the employee by employee ID.
  all
    .filter((contract) => !byId.has(contract.id))
    .forEach((contract) => {
      const employee = (employees || []).find((user) => user?.employeeid === contract.employeeid);
      if (!employee) return;
      byId.set(contract.id, {
        ...contract,
        appraiseeuserid: employee.userid,
        appraiseruserid: null,
        progressreviewdates: [],
        declinereason: null,
      });
    });
  return [...byId.values()].sort((a, b) => b.id - a.id);
};

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;

  .filters {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  .ant-input-affix-wrapper,
  .ant-input,
  .ant-picker,
  .ant-select .ant-select-selector {
    border-radius: 24px !important;
  }

  .ant-input::placeholder,
  .ant-picker-input > input::placeholder,
  .ant-select-selection-placeholder {
    color: #9ca3af !important;
  }
`;

const StyledTable = styled(Table)`
  .ant-table {
    border: 1px solid #ccc;
    border-radius: 12px;
    overflow: hidden;
  }
  .ant-table-container {
    border-radius: 12px;
  }
  .ant-table-content {
    overflow-x: auto;
  }
  .ant-table-cell {
    padding: 16px;
    white-space: nowrap;
  }
  .ant-table-thead .ant-table-cell {
    font-weight: 600;
    color: ${COLORS.SECONDARY_BLACK};
  }
`;

const ActionWrapper = styled.div`
  opacity: 0;
  transition: opacity 0.2s ease;
  display: flex;
  align-items: center;
  gap: 14px;
  justify-content: flex-end;

  .ant-table-row:hover & {
    opacity: 1;
  }

  .action-icon {
    cursor: pointer;
    color: ${COLORS.PRIMARY};
    font-size: 16px;
  }
`;

const ICONS = {
  edit: EditOutlined,
  delete: DeleteOutlined,
  calendar: CalendarOutlined,
};

// Which menu permission each row action needs - only enforced when a
// `permission` object is passed (the HR tab); My Team keeps every action.
const ACTION_PERMISSION = { edit: "edit", calendar: "edit", delete: "delete" };
const primaryActionPermission = (status) => (status === "notyetcreated" ? "create" : "edit");
const allows = (permission, needed) => !permission || permission[needed] === true;

const MyTeamRowActions = ({ status, onAction, permission }) => {
  const action = KPI_PLANNING_MYTEAM_ACTIONS[status];
  if (!action) return null;
  const showPrimary = allows(permission, primaryActionPermission(status));

  return (
    <ActionWrapper>
      {(action.type === "link" || action.type === "link+icons") && showPrimary && (
        <TextLink onClick={(e) => onAction(e, "primary")}>{action.label}</TextLink>
      )}
      {(action.type === "icons" || action.type === "link+icons") &&
        action.icons
          .filter((iconKey) => allows(permission, ACTION_PERMISSION[iconKey]))
          .map((iconKey) => {
            const Icon = ICONS[iconKey];
            return (
              <Icon
                key={iconKey}
                className="action-icon"
                onClick={(e) => onAction(e, iconKey)}
              />
            );
          })}
    </ActionWrapper>
  );
};

const MineRowActions = ({ status, onAction }) => {
  const action = KPI_PLANNING_MINE_ACTIONS[status];
  if (!action) return null;

  return (
    <ActionWrapper>
      {action.type === "link+info" && (
        <Popover content={action.tooltip}>
          <InfoCircleFilled className="action-icon" />
        </Popover>
      )}
      <TextLink onClick={(e) => onAction(e, "primary")}>{action.label}</TextLink>
      {action.icons?.map((iconKey) => {
        const Icon = ICONS[iconKey];
        return (
          <Icon key={iconKey} className="action-icon" onClick={(e) => onAction(e, iconKey)} />
        );
      })}
    </ActionWrapper>
  );
};

const PERIOD_OPTIONS = [
  { label: "Yearly", value: "Yearly" },
  { label: "Half Yearly", value: "Half Yearly" },
  { label: "Quarterly", value: "Quarterly" },
];

const formatDate = (value) => (value ? dayjs(value).format("Do MMM YYYY") : "-");

// Always the NEXT not-yet-completed checkpoint's date - once Q1 closes, this
// should move on to show Mid Year's date instead of staying stuck on Q1's.
// Falls back to the last checkpoint once every one of them is completed.
const upcomingReviewDate = (progressreviewdates) => {
  const dates = Array.isArray(progressreviewdates) ? progressreviewdates : [];
  if (!dates.length) return null;
  const next = dates.find((item) => !item.completed);
  return (next || dates[dates.length - 1])?.date;
};

const StatusCell = ({ status, map, reason }) => {
  const meta = statusMeta(map, status);
  return (
    <span style={{ color: meta.color, fontWeight: 500 }}>
      {meta.label}
      {status === "declined" && reason && (
        <Popover content={reason} title="Reason for decline">
          <InfoCircleFilled style={{ marginLeft: 6, color: COLORS.SECONDARY_BLACK }} />
        </Popover>
      )}
    </span>
  );
};

const MineKpiPlanning = () => {
  const history = useHistory();
  const userInfo = useSelector(getUserInfo);
  const clientid = useSelector(getClientid);
  const [dataList, setDataList] = useState([]);
  const [me, setMe] = useState(null);
  const [filterList, setFilterList] = useState([]);
  const [loading, setLoading] = useState(false);
  // The self-KPI placeholder row depends on `me`, so the list isn't final
  // until the roster call is done too.
  const [rosterLoading, setRosterLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [period, setPeriod] = useState();
  const [date, setDate] = useState();
  const [popupOpen, setPopupOpen] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data, code } = await CALL_API(
        `performance-kpi-management/mineplanning/${userInfo?.userid}`,
        "get"
      );
      setDataList(code === STATUS_CODE.SUCCESS && Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching KPI planning list:", error);
      setDataList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userInfo?.userid) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?.userid]);

  useEffect(() => {
    if (!clientid || !userInfo?.userid) return;
    setRosterLoading(true);
    fetchEmployeeList(clientid)
      .then((list) => setMe(list.find((u) => u.userid === userInfo.userid) || null))
      .catch((error) => console.error("Error fetching employee list:", error))
      .finally(() => setRosterLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientid, userInfo?.userid]);

  // A "notyetcreated" row can exist for real (e.g. generated before the
  // employee's self-KPI flag was turned on) - if the employee's *current*
  // isemployeeselfkpi is true, it should read as "Awaiting your KPI
  // Submission" regardless of what it was when the row was created.
  const isSelfKpi = me?.isemployeeselfkpi;
  // Self-KPI employees have no manager-driven "generate next cycle" step -
  // once their latest cycle's reviews are all closed, there's nothing left
  // to prompt them to submit again unless a fresh placeholder is added here,
  // same as the very-first-ever-contract case below.
  const needsNewSelfKpiCycle =
    isSelfKpi && (dataList.length === 0 || dataList[0]?.planningstatus === "reviewsclosed");
  const awaitingSelfKpiRow = {
    id: `awaitingkpisubmission-${userInfo?.userid}-${dataList.length}`,
    period: null,
    createdon: null,
    progressreviewdates: [],
    startdate: null,
    planningstatus: "awaitingkpisubmission",
  };
  const mergedList = needsNewSelfKpiCycle
    ? [
        awaitingSelfKpiRow,
        // "Awaiting Progress Review" / "In Progress Review" / "Reviews Closed"
        // are My Team-only statuses - collapse to "Accepted" here same as below.
        ...dataList.map((record) =>
          MINE_HIDDEN_STATUSES.includes(record.planningstatus)
            ? { ...record, planningstatus: "accepted" }
            : record
        ),
      ]
    : // "Awaiting Progress Review" / "In Progress Review" / "Reviews Closed"
      // are My Team-only statuses (progress-review bookkeeping the manager
      // acts on) - per design, "Mine" only ever shows the 6 appraisee-facing
      // statuses, so all three collapse to "Accepted" here.
      dataList.map((record) => {
        let planningstatus = record.planningstatus;
        if (planningstatus === "notyetcreated" && isSelfKpi) {
          planningstatus = "awaitingkpisubmission";
        } else if (MINE_HIDDEN_STATUSES.includes(planningstatus)) {
          planningstatus = "accepted";
        }
        return { ...record, planningstatus };
      });

  useEffect(() => {
    let filtered = [...mergedList];
    if (search) {
      filtered = filtered.filter((item) =>
        item?.period?.toLowerCase().includes(search.toLowerCase())
      );
    }
    if (period) {
      filtered = filtered.filter((item) => item?.period === period);
    }
    if (date) {
      filtered = filtered.filter((item) =>
        dayjs(item?.createdon).isSame(dayjs(date), "day")
      );
    }
    setFilterList(filtered);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, period, date, dataList, isSelfKpi]);

  const navigateForRecord = (record) => {
    if (
      record.planningstatus === "awaitingkpisubmission" ||
      record.planningstatus === "revisionrequested"
    ) {
      history.push("/performance-kpi-fill", {
        contractid: typeof record.id === "number" ? record.id : undefined,
      });
    } else {
      history.push("/performance-kpi-review", { contractid: record.id });
    }
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: "Are you sure you want to delete this KPI Planning?",
      icon: null,
      centered: true,
      okText: "Yes",
      cancelText: "No",
      okButtonProps: { type: "primary" },
      onOk: async () => {
        try {
          const { code, message: apiMessage } = await CALL_API(
            `performance-kpi-management/contract/${record.id}`,
            "delete"
          );
          if (code === STATUS_CODE.SUCCESS) {
            setPopupOpen(true);
            setTimeout(() => setPopupOpen(false), 1500);
            fetchData();
          } else {
            message.error(apiMessage || "Unable to delete this KPI Planning");
          }
        } catch (error) {
          console.error("Error deleting contract:", error);
          message.error("Unable to delete this KPI Planning");
        }
      },
    });
  };

  const columns = [
    { title: "Period", dataIndex: "period", key: "period", render: (t) => t || "-" },
    { title: "Created on", dataIndex: "createdon", key: "createdon", render: formatDate },
    {
      title: "Progress Review on",
      dataIndex: "progressreviewdates",
      key: "progressreviewdates",
      render: (val) => formatDate(upcomingReviewDate(val)),
    },
    { title: "Attend Appraisal from", dataIndex: "startdate", key: "startdate", render: formatDate },
    {
      title: "Status",
      dataIndex: "planningstatus",
      key: "planningstatus",
      render: (status, record) => (
        <StatusCell status={status} map={KPI_PLANNING_MINE_STATUS} reason={record.declinereason} />
      ),
    },
    {
      key: "actions",
      width: 180,
      render: (_, record) => (
        <MineRowActions
          status={record.planningstatus}
          onAction={(e, action) => {
            e.stopPropagation();
            if (action === "delete") {
              handleDelete(record);
            } else if (action === "edit") {
              history.push("/performance-kpi-fill", { contractid: record.id });
            } else {
              navigateForRecord(record);
            }
          }}
        />
      ),
    },
  ];

  return (
    <div>
      <FilterBar>
        <div className="lite-text">
          Total Role: {loading || rosterLoading ? "-" : mergedList.length}
        </div>
        <div className="filters">
          <Input
            placeholder="Search"
            prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
            style={{ width: 200 }}
          />
          <Select
            placeholder="Period"
            allowClear
            options={PERIOD_OPTIONS}
            value={period}
            onChange={setPeriod}
            style={{ width: 140 }}
          />
          <DatePicker placeholder="Date" value={date} onChange={setDate} />
        </div>
      </FilterBar>
      <Spin spinning={loading || rosterLoading}>
        <StyledTable
          columns={columns}
          dataSource={loading || rosterLoading ? [] : filterList}
          locale={loading || rosterLoading ? { emptyText: " " } : undefined}
          pagination={false}
          scroll={{ x: "max-content" }}
          rowKey={(record) => record.id}
          onRow={(record) => ({
            style: { cursor: "pointer" },
            onClick: () => navigateForRecord(record),
          })}
        />
      </Spin>
      <SuccessDeclinePopup open={popupOpen} setOpen={setPopupOpen} type="success" message="Deleted" />
    </div>
  );
};

// scope "myteam": the logged-in user's direct reports (contracts where they're
// the appraiser). scope "hr": every active employee and every contract,
// gated by the Performance "HR" menu permission passed in as `permission`.
const TeamKpiPlanning = ({ scope = "myteam", permission }) => {
  const isHr = scope === "hr";
  const history = useHistory();
  const userInfo = useSelector(getUserInfo);
  const clientid = useSelector(getClientid);
  const [employeeList, setEmployeeList] = useState([]);
  const [dataList, setDataList] = useState([]);
  const [freshRows, setFreshRows] = useState([]);
  const [filterList, setFilterList] = useState([]);
  // HR only starts fetching once the roster is in, so it starts out loading.
  const [loading, setLoading] = useState(isHr);
  // Every row is built from the roster, so the list isn't final until the
  // roster call is done too.
  const [rosterLoading, setRosterLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [period, setPeriod] = useState();
  const [date, setDate] = useState();
  const [status, setStatus] = useState();
  const [kpiCreatedBy, setKpiCreatedBy] = useState();
  const [popupOpen, setPopupOpen] = useState(false);
  const [adjustDatesRecord, setAdjustDatesRecord] = useState(null);
  const [generateResult, setGenerateResult] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (isHr) {
        setDataList(await fetchAllTeamPlanning(employeeList));
        return;
      }
      const { data, code } = await CALL_API(
        `performance-kpi-management/myteamplanning/${userInfo?.userid}`,
        "get"
      );
      setDataList(code === STATUS_CODE.SUCCESS && Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching KPI planning team list:", error);
      setDataList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isHr && userInfo?.userid) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?.userid]);

  // The HR list is built per reporting manager, so it waits for the roster.
  useEffect(() => {
    if (isHr && employeeList.length) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeList]);

  useEffect(() => {
    if (!clientid) return;
    setRosterLoading(true);
    fetchEmployeeList(clientid)
      .then(setEmployeeList)
      .catch((error) => console.error("Error fetching employee list:", error))
      .finally(() => setRosterLoading(false));
  }, [clientid]);

  // Direct reports come from a fresh call to the People module's employee
  // list (not Redux's userList, which is only populated once at login and
  // never refreshed) so an isemployeeselfkpi toggle made afterwards in
  // People is reflected here right away. The HR tab covers every active
  // employee instead of only the logged-in user's direct reports.
  const directReports = useMemo(
    () =>
      (employeeList || []).filter(
        (user) =>
          user?.statusid === "1" &&
          (isHr || user?.reportingmanager === userInfo?.userid)
      ),
    [employeeList, userInfo, isHr]
  );

  // Every direct report always appears - either with their real contract(s),
  // a virtual "Not Yet Created" placeholder when they have none yet, or a
  // freshly-started-cycle placeholder from "Load New List".
  const mergedList = useMemo(() => {
    const rows = directReports.flatMap((report) => {
      const matched = dataList.filter(
        (contract) => contract.appraiseeuserid === report.userid
      );
      // myteamplanning doesn't select isemployeeselfkpi (it's a People-side
      // flag, not a contract column) - blankTeamRow already carries it for
      // placeholder rows, but real contract rows need it stamped on here
      // too so the "KPI Created By" filter has something consistent to read
      // regardless of whether the employee has actually submitted yet.
      return matched.length
        ? matched.map((contract) => ({ ...contract, isemployeeselfkpi: report.isemployeeselfkpi }))
        : [blankTeamRow(report)];
    });
    // Newly generated rows go first so they're immediately visible instead
    // of buried at the bottom of the list.
    return applyDerivedStatus([...freshRows, ...rows]);
  }, [directReports, dataList, freshRows]);

  useEffect(() => {
    let filtered = [...mergedList];
    if (search) {
      filtered = filtered.filter((item) =>
        item?.fullname?.toLowerCase().includes(search.toLowerCase())
      );
    }
    if (period) {
      filtered = filtered.filter((item) => item?.period === period);
    }
    if (date) {
      filtered = filtered.filter((item) =>
        dayjs(item?.createdon).isSame(dayjs(date), "day")
      );
    }
    if (status) {
      filtered = filtered.filter((item) => item?.planningstatus === status);
    }
    if (kpiCreatedBy && kpiCreatedBy !== "all") {
      // kpidefinedby only gets set once a contract has actually been
      // submitted/created, so filtering on it hid every "Not Yet Created" /
      // "Awaiting Appraisee KPI Submission" placeholder row - isemployeeselfkpi
      // is the employee's own designation and is always present, whether or
      // not they've submitted anything yet.
      filtered = filtered.filter((item) =>
        kpiCreatedBy === "Self" ? Boolean(item?.isemployeeselfkpi) : !item?.isemployeeselfkpi
      );
    }
    setFilterList(filtered);
  }, [search, period, date, status, kpiCreatedBy, mergedList]);

  const statusOptions = Object.entries(KPI_PLANNING_MYTEAM_STATUS).map(
    ([value, meta]) => ({ label: meta.label, value })
  );

  const kpiCreatedByOptions = [
    { label: "All", value: "all" },
    { label: "Self", value: "Self" },
    { label: "Appraiser", value: "Appraiser" },
  ];

  // Mirrors the old Performance module's "Load New List": computed entirely
  // client-side from the already-loaded team roster and their latest known
  // contract, with no dedicated "generate" API call.
  const handleLoadNewList = () => {
    const skipped = [];
    const newRows = [];

    directReports.forEach((report) => {
      const contracts = dataList.filter(
        (contract) => contract.appraiseeuserid === report.userid
      );
      if (!contracts.length) return;

      const latest = contracts.reduce((max, curr) =>
        curr.id > max.id ? curr : max
      );

      if (OPEN_PLANNING_STATUSES.includes(latest.planningstatus)) {
        skipped.push({ fullname: latest.fullname, employeeid: latest.employeeid });
      } else if (latest.planningstatus === "reviewsclosed") {
        newRows.push({ ...blankTeamRow(report), id: `fresh-${report.userid}-${Date.now()}` });
      }
    });

    setFreshRows((prev) => [
      ...prev.filter(
        (row) => !newRows.some((n) => n.appraiseeuserid === row.appraiseeuserid)
      ),
      ...newRows,
    ]);

    if (newRows.length === 0) {
      message.info("No List generated");
    } else if (skipped.length > 0) {
      setGenerateResult({ generated: newRows.length, skipped });
    } else {
      message.success(`${newRows.length} new KPI planning list(s) generated`);
    }
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: "Are you sure you want to delete this KPI Planning?",
      icon: null,
      centered: true,
      okText: "Yes",
      cancelText: "No",
      okButtonProps: { type: "primary" },
      onOk: async () => {
        try {
          const { code, message: apiMessage } = await CALL_API(
            `performance-kpi-management/contract/${record.id}`,
            "delete"
          );
          if (code === STATUS_CODE.SUCCESS) {
            setPopupOpen(true);
            setTimeout(() => setPopupOpen(false), 1500);
            fetchData();
          } else {
            message.error(apiMessage || "Unable to delete this KPI Planning");
          }
        } catch (error) {
          console.error("Error deleting contract:", error);
          message.error("Unable to delete this KPI Planning");
        }
      },
    });
  };

  // fromTab tells the opened page which tab its Back / Done returns to.
  const navigateForRecord = (record) => {
    if (!allows(permission, primaryActionPermission(record.planningstatus))) return;
    if (record.planningstatus === "notyetcreated") {
      history.push("/performance-kpi-create-contract", { record, fromTab: scope });
    } else if (record.planningstatus === "awaitingkpisubmission") {
      // Self-KPI employee hasn't submitted anything yet - there's no
      // contract content for the manager to view, so this row isn't
      // navigable (mirrors "notyetcreated" having nothing to view either,
      // except here the manager also can't create it on the employee's
      // behalf).
      return;
    } else if (record.planningstatus === "awaitingkpireview") {
      history.push("/performance-kpi-review-kpi", { contractid: record.id, fromTab: scope });
    } else if (
      ["awaitingprogressreview", "inprogressreview", "reviewsclosed"].includes(
        record.planningstatus
      )
    ) {
      history.push("/performance-kpi-review-progress", { contractid: record.id, fromTab: scope });
    } else {
      // My Team is a read-only view of the appraisee's contract - Accept/
      // Decline are their own actions, not the manager's.
      history.push("/performance-kpi-review", {
        contractid: record.id,
        viewOnly: true,
        fromTab: scope,
      });
    }
  };

  const nameFor = (userid) =>
    (employeeList || []).find((user) => user?.userid === userid)?.fullname || userid || "-";

  const columns = [
    { title: "Name", dataIndex: "fullname", key: "fullname", render: (t) => t || "-" },
    { title: "Employee ID", dataIndex: "employeeid", key: "employeeid", render: (t) => t || "-" },
    isHr && {
      title: "Reporting Manager",
      dataIndex: "appraiseruserid",
      key: "appraiseruserid",
      render: nameFor,
    },
    { title: "Period", dataIndex: "period", key: "period", render: (t) => t || "-" },
    { title: "Created on", dataIndex: "createdon", key: "createdon", render: formatDate },
    {
      title: "Progress Review on",
      dataIndex: "progressreviewdates",
      key: "progressreviewdates",
      render: (val) => formatDate(upcomingReviewDate(val)),
    },
    { title: "Attend Appraisal from", dataIndex: "startdate", key: "startdate", render: formatDate },
    {
      title: "Status",
      dataIndex: "planningstatus",
      key: "planningstatus",
      render: (s, record) => (
        <StatusCell status={s} map={KPI_PLANNING_MYTEAM_STATUS} reason={record.declinereason} />
      ),
    },
    {
      key: "actions",
      width: 220,
      render: (_, record) => (
        <MyTeamRowActions
          status={record.planningstatus}
          permission={permission}
          onAction={(e, action) => {
            e.stopPropagation();
            if (action === "primary") {
              navigateForRecord(record);
            } else if (action === "delete") {
              handleDelete(record);
            } else if (action === "edit") {
              history.push("/performance-kpi-create-contract", {
                contractid: record.id,
                fromTab: scope,
              });
            } else if (action === "calendar") {
              setAdjustDatesRecord(record);
            }
          }}
        />
      ),
    },
  ].filter(Boolean);

  return (
    <div>
      <FilterBar>
        <div className="lite-text">
          List of KPI: {loading || rosterLoading ? "-" : mergedList.length}
        </div>
        <div className="filters">
          <Input
            placeholder="Search"
            prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
            style={{ width: 180 }}
          />
          <Select
            placeholder="Period"
            allowClear
            options={PERIOD_OPTIONS}
            value={period}
            onChange={setPeriod}
            style={{ width: 130 }}
          />
          <DatePicker placeholder="Date" value={date} onChange={setDate} />
          <Select
            placeholder="Status"
            allowClear
            options={statusOptions}
            value={status}
            onChange={setStatus}
            style={{ width: 160 }}
          />
          <Select
            placeholder="KPI Created By"
            allowClear
            options={kpiCreatedByOptions}
            value={kpiCreatedBy}
            onChange={setKpiCreatedBy}
            style={{ width: 160 }}
          />
          {allows(permission, "create") && (
            <ButtonFilled
              type="primary"
              onClick={handleLoadNewList}
              disabled={loading || rosterLoading}
            >
              <ReloadOutlined /> Load New List
            </ButtonFilled>
          )}
        </div>
      </FilterBar>
      <Spin spinning={loading || rosterLoading}>
        <StyledTable
          columns={columns}
          dataSource={loading || rosterLoading ? [] : filterList}
          locale={loading || rosterLoading ? { emptyText: " " } : undefined}
          pagination={false}
          scroll={{ x: "max-content" }}
          rowKey={(record) => record.id}
          onRow={(record) => ({
            style: {
              cursor: record.planningstatus === "awaitingkpisubmission" ? "default" : "pointer",
            },
            onClick: () => navigateForRecord(record),
          })}
        />
      </Spin>
      <SuccessDeclinePopup
        open={popupOpen}
        setOpen={setPopupOpen}
        type="success"
        message="Deleted"
      />
      <AdjustDatesModal
        open={Boolean(adjustDatesRecord)}
        record={adjustDatesRecord}
        onClose={() => setAdjustDatesRecord(null)}
        onSuccess={fetchData}
      />
      <GenerateListModal
        open={Boolean(generateResult)}
        result={generateResult}
        onClose={() => setGenerateResult(null)}
      />
    </div>
  );
};

const KpiPlanningList = () => {
  const location = useLocation();
  const menuList = useSelector(getMenuList);
  const userRoleId = useSelector(getUserRoleId);

  // Mirrors the old Performance module's Mine/My Team permission gating -
  // reuses the same "myperformance" / "myteamperformance" menu keys. The HR
  // tab (every employee, not just direct reports) has its own
  // "hrkpiperformance" key.
  const Menulist = menuList[userRoleId] || Object.values(menuList)[0] || menuList;
  const isMine = Menulist?.find((item) => item?.key === "myperformance" && item?.permission?.view);
  const isMyteam = Menulist?.find(
    (item) => item?.key === "myteamperformance" && item?.permission?.view
  );
  const hrMenu = Menulist?.find((item) => isHrKpiMenu(item) && item?.permission?.view);

  const [tabId, setTabId] = useState(
    location.state?.subTab || (isMine ? "mine" : isMyteam ? "myteam" : "hr")
  );

  useEffect(() => {
    if (location.state?.subTab) setTabId(location.state.subTab);
  }, [location.state]);

  const items = [
    isMine && { key: "mine", label: "Mine", children: <MineKpiPlanning /> },
    isMyteam && { key: "myteam", label: "My Team", children: <TeamKpiPlanning scope="myteam" /> },
    hrMenu && {
      key: "hr",
      label: "HR",
      children: <TeamKpiPlanning scope="hr" permission={hrMenu.permission} />,
    },
  ].filter(Boolean);

  return <Tabs activeKey={tabId} items={items} onChange={setTabId} />;
};

export default KpiPlanningList;
